import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, Trash2, Plus, X } from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { LEAD_TYPE_LABEL } from "../components/leads/leadConfig";

const EMPTY = {
  name: "", contact_name: "", email: "", phone: "", website: "",
  lead_types: [], regions: [],
  min_property_value: "", max_property_value: "",
  price_per_lead: "", exclusivity: "shared", max_shared_count: 3,
  active: true, is_primary_mortgage_partner: false, internal_notes: "",
};

const Section = ({ title, children }) => (
  <div className="rounded-3xl border border-border bg-white p-5 shadow-sm">
    <h3 className="font-display text-sm font-extrabold uppercase tracking-wide text-slate-500">{title}</h3>
    <div className="mt-4 grid gap-3 sm:grid-cols-2">{children}</div>
  </div>
);

const Row = ({ label, children, span = 1 }) => (
  <div className={span === 2 ? "sm:col-span-2" : ""}>
    <label className="text-xs font-semibold text-slate-600">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const AdminPartnerDetail = () => {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  useSeo({ title: isNew ? "Nieuwe partner · Admin" : "Partner · Admin", description: "Partner beheren" });

  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [newRegion, setNewRegion] = useState("");

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    api(`/admin/partners/${id}`).then((d) => {
      setForm({ ...EMPTY, ...d,
        min_property_value: d.min_property_value ?? "",
        max_property_value: d.max_property_value ?? "",
        price_per_lead: d.price_per_lead ?? "",
      });
      setLoading(false);
    }).catch((e) => { toast.error(e.message); setLoading(false); });
  }, [id, isNew]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleType = (t) => setForm((f) => {
    const has = f.lead_types.includes(t);
    return { ...f, lead_types: has ? f.lead_types.filter((x) => x !== t) : [...f.lead_types, t] };
  });

  const addRegion = () => {
    const r = newRegion.trim();
    if (!r) return;
    if (!form.regions.includes(r)) set("regions", [...form.regions, r]);
    setNewRegion("");
  };
  const removeRegion = (r) => set("regions", form.regions.filter((x) => x !== r));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Bedrijfsnaam is verplicht"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        min_property_value: form.min_property_value === "" ? null : Number(form.min_property_value),
        max_property_value: form.max_property_value === "" ? null : Number(form.max_property_value),
        price_per_lead: form.price_per_lead === "" ? null : Number(form.price_per_lead),
      };
      if (isNew) {
        const d = await api("/admin/partners", { method: "POST", body: JSON.stringify(payload) });
        toast.success("Partner aangemaakt");
        navigate(`/admin/partners/${d.id}`, { replace: true });
      } else {
        await api(`/admin/partners/${id}`, { method: "PUT", body: JSON.stringify(payload) });
        toast.success("Opgeslagen");
      }
    } catch (e) {
      toast.error(e.message || "Opslaan mislukt");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Deze partner permanent verwijderen?")) return;
    setBusy(true);
    try {
      await api(`/admin/partners/${id}`, { method: "DELETE" });
      toast.success("Verwijderd");
      navigate("/admin/partners");
    } catch (e) {
      toast.error(e.message); setBusy(false);
    }
  };

  if (loading) return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="animate-spin text-brandblue" size={32} /></div>;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/admin/partners" className="inline-flex items-center gap-1 hover:text-navy"><ArrowLeft size={14} /> Partners</Link>
      </div>
      <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
        <h1 className="font-display text-3xl font-extrabold text-navy">{isNew ? "Nieuwe partner" : form.name}</h1>
        {!isNew && (
          <Button variant="ghost" size="sm" onClick={del} className="text-danger" disabled={busy}>
            <Trash2 size={14} className="mr-1" /> Verwijderen
          </Button>
        )}
      </div>

      <div className="mt-6 space-y-6">
        <Section title="Organisatie">
          <Row label="Bedrijfsnaam *" span={2}>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-xl" data-testid="p-name" />
          </Row>
          <Row label="Contactpersoon">
            <Input value={form.contact_name || ""} onChange={(e) => set("contact_name", e.target.value)} className="rounded-xl" data-testid="p-contact" />
          </Row>
          <Row label="Website">
            <Input value={form.website || ""} onChange={(e) => set("website", e.target.value)} className="rounded-xl" placeholder="https://…" data-testid="p-website" />
          </Row>
          <Row label="E-mail">
            <Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} className="rounded-xl" data-testid="p-email" />
          </Row>
          <Row label="Telefoon">
            <Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className="rounded-xl" data-testid="p-phone" />
          </Row>
        </Section>

        <Section title="Type leads dat deze partner accepteert">
          <div className="sm:col-span-2 flex flex-wrap gap-2">
            {Object.entries(LEAD_TYPE_LABEL).map(([k, v]) => {
              const on = form.lead_types.includes(k);
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => toggleType(k)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${on ? "border-brandblue bg-brandblue text-white" : "border-border bg-white text-slate-500 hover:border-brandblue hover:text-brandblue"}`}
                  data-testid={`p-type-${k}`}
                >
                  {v}
                </button>
              );
            })}
          </div>
        </Section>

        <Section title="Werkgebied">
          <div className="sm:col-span-2">
            <div className="flex gap-2">
              <Input value={newRegion} onChange={(e) => setNewRegion(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addRegion())} placeholder="Plaats of provincie" className="rounded-xl" data-testid="p-region-input" />
              <Button type="button" onClick={addRegion} variant="outline" className="rounded-xl"><Plus size={14} className="mr-1" /> Toevoegen</Button>
            </div>
            {form.regions.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {form.regions.map((r) => (
                  <span key={r} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                    {r}
                    <button type="button" onClick={() => removeRegion(r)} className="rounded-full p-0.5 hover:bg-slate-200"><X size={12} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </Section>

        <Section title="Woningwaarde & prijsafspraak">
          <Row label="Minimale woningwaarde (€)">
            <Input type="number" min="0" value={form.min_property_value ?? ""} onChange={(e) => set("min_property_value", e.target.value)} className="rounded-xl" placeholder="200000" data-testid="p-minv" />
          </Row>
          <Row label="Maximale woningwaarde (€)">
            <Input type="number" min="0" value={form.max_property_value ?? ""} onChange={(e) => set("max_property_value", e.target.value)} className="rounded-xl" placeholder="1500000" data-testid="p-maxv" />
          </Row>
          <Row label="Prijs per lead (€)">
            <Input type="number" min="0" step="0.01" value={form.price_per_lead ?? ""} onChange={(e) => set("price_per_lead", e.target.value)} className="rounded-xl" placeholder="39.00" data-testid="p-price" />
          </Row>
          <Row label="Exclusiviteit">
            <select value={form.exclusivity} onChange={(e) => set("exclusivity", e.target.value)} className="w-full rounded-xl border border-border bg-white px-3 py-2 text-sm" data-testid="p-excl">
              <option value="shared">Gedeeld (meerdere partners)</option>
              <option value="exclusive">Exclusief (alleen deze partner)</option>
            </select>
          </Row>
          {form.exclusivity === "shared" && (
            <Row label="Max. aantal partners per lead">
              <Input type="number" min="1" max="10" value={form.max_shared_count ?? 3} onChange={(e) => set("max_shared_count", Number(e.target.value) || 3)} className="rounded-xl" data-testid="p-shared" />
            </Row>
          )}
        </Section>

        <Section title="Status & notities">
          <Row label="Status" span={2}>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} data-testid="p-active" />
              Actief — ontvangt leads
            </label>
          </Row>
          {form.lead_types.includes("mortgage") && (
            <Row label="Hypotheek-routing" span={2}>
              <label className="inline-flex items-start gap-2 text-sm">
                <input type="checkbox" checked={!!form.is_primary_mortgage_partner} onChange={(e) => set("is_primary_mortgage_partner", e.target.checked)} data-testid="p-primary-mortgage" />
                <span>
                  Primaire hypotheekpartner — ontvangt automatisch alle leads uit de maandlasten-widget.
                  <span className="mt-0.5 block text-[11px] text-slate-400">Slechts één partner tegelijk; aanzetten schakelt anderen automatisch uit.</span>
                </span>
              </label>
            </Row>
          )}
          <Row label="Interne notities" span={2}>
            <textarea value={form.internal_notes || ""} onChange={(e) => set("internal_notes", e.target.value)} rows={4} className="w-full rounded-xl border border-border bg-white px-3 py-2 text-sm" data-testid="p-notes" />
          </Row>
        </Section>

        <div className="flex justify-end gap-3">
          <Button variant="outline" asChild className="rounded-xl"><Link to="/admin/partners">Annuleer</Link></Button>
          <Button onClick={save} disabled={busy} className="rounded-xl bg-navy text-amber-300 hover:bg-navy/90" data-testid="p-save">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <><Save size={14} className="mr-1.5" /> {isNew ? "Partner aanmaken" : "Opslaan"}</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminPartnerDetail;
